/**
 * Small shared UI primitives - loading, errors, empty states, form fields,
 * pills, list footers, and time/identity display.
 *
 * These are the names later work builds on (docs/FRONTEND-PLAN.md section 2.1) -
 * add to this file rather than re-inventing a variant inside a page. Every
 * visual value is a dcon design-system utility from frontend/src/lib/ui.js
 * (docs/Decisions.md D41); nothing here carries inline style.
 */
import { useId, useState } from 'react';
import { ApiError } from '../lib/apiClient';
import { lookupUsers } from '../api/users';
import { cn, ui } from '../lib/ui';

export function Spinner({ label = 'Loading…' }) {
  return (
    <p className={ui.muted} role="status">
      {label}
    </p>
  );
}

// `message` is accepted as an alias of `error` (and `children` on
// EmptyState): several pages pass `message=`, which used to render nothing.
export function ErrorAlert({ error: errorProp, message: messageProp }) {
  const error = errorProp || messageProp;
  if (!error) return null;
  const message =
    error instanceof ApiError || error instanceof Error ? error.message : String(error);
  // Validation failures (e.g. PUT /admin/site-settings) carry a human-
  // readable list in ApiError.details - surface each one, not just the
  // generic "Validation failed" message.
  const details = error instanceof ApiError && Array.isArray(error.details) ? error.details : null;
  return (
    <div className={ui.alertError} role="alert">
      <p className="m-0">{message}</p>
      {details && details.length > 0 && (
        <ul className="mt-2 mb-0 list-disc pl-5">
          {details.map((detail, index) => (
            <li key={index}>{String(detail)}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function EmptyState({ children, message }) {
  return <p className={ui.muted}>{children ?? message}</p>;
}

/**
 * The footer of a `usePagedList` list: a Load-more button, the appending
 * spinner, or the end-of-list note. Drop it straight under the rows:
 *
 *   <PagedListFooter {...list} onLoadMore={list.loadMore} endLabel="No more threads." />
 *
 * Pass `endLabel={null}` to render nothing once the list is exhausted.
 */
const pagedFooter = 'mt-4 text-center';

export function PagedListFooter({
  hasMore,
  loadingMore,
  onLoadMore,
  label = 'Load more',
  endLabel = 'End of list.',
}) {
  if (loadingMore) {
    return (
      <p className={cn(ui.muted, pagedFooter)} role="status">
        Loading more…
      </p>
    );
  }
  if (hasMore) {
    return (
      <div className={pagedFooter}>
        <button type="button" className={ui.btnSecondary} onClick={onLoadMore}>
          {label}
        </button>
      </div>
    );
  }
  if (!endLabel) return null;
  return <p className={cn(ui.muted, 'text-body-s', pagedFooter)}>{endLabel}</p>;
}

/**
 * Labelled text input. `hint` is wired to the control with aria-describedby so
 * screen readers announce it - the three Field variants below are identical in
 * shape (label, hint, everything else passed through to the control) and only
 * differ in the element.
 */
/**
 * The hint and error lines under a control, and the aria wiring that ties
 * them to it. `error` is a message (a string) the form shows beside the field
 * it belongs to (docs/Decisions.md D63 item 3); the control is marked invalid.
 */
function useFieldAria(hint, error) {
  const hintId = useId();
  const errorId = useId();
  const describedBy = [hint ? hintId : null, error ? errorId : null].filter(Boolean).join(' ');
  return {
    controlProps: {
      'aria-describedby': describedBy || undefined,
      'aria-invalid': error ? true : undefined,
    },
    className: cn(ui.input, error && ui.inputInvalid),
    after: (
      <>
        {hint && (
          <span className={ui.hint} id={hintId}>
            {hint}
          </span>
        )}
        {error && (
          <span className={ui.fieldError} id={errorId} role="alert">
            {error}
          </span>
        )}
      </>
    ),
  };
}

export function Field({ label, hint, error, ...inputProps }) {
  const aria = useFieldAria(hint, error);
  return (
    <label className={ui.field}>
      <span className={ui.label}>{label}</span>
      <input className={aria.className} {...aria.controlProps} {...inputProps} />
      {aria.after}
    </label>
  );
}

export function TextareaField({ label, hint, error, ...textareaProps }) {
  const aria = useFieldAria(hint, error);
  return (
    <label className={ui.field}>
      <span className={ui.label}>{label}</span>
      <textarea className={aria.className} {...aria.controlProps} {...textareaProps} />
      {aria.after}
    </label>
  );
}

/** Options are the children: `<SelectField label="Type"><option ... /></SelectField>`. */
export function SelectField({ label, hint, error, children, ...selectProps }) {
  const aria = useFieldAria(hint, error);
  return (
    <label className={ui.field}>
      <span className={ui.label}>{label}</span>
      <select className={aria.className} {...aria.controlProps} {...selectProps}>
        {children}
      </select>
      {aria.after}
    </label>
  );
}

/** Map a status string onto a pill class (frontend/src/lib/ui.js). */
export function StatusPill({ status }) {
  const tone =
    {
      approved: ui.pillSuccess,
      active: ui.pillSuccess,
      verified: ui.pillSuccess,
      completed: ui.pillSuccess,
      accepted: ui.pillSuccess,
      checked_in: ui.pillSuccess,
      pending: ui.pillWarning,
      submitted: ui.pillWarning,
      on_hold: ui.pillWarning,
      tentative: ui.pillWarning,
      high: ui.pillWarning,
      under_review: ui.pillInfo,
      claimed: ui.pillInfo,
      in_progress: ui.pillInfo,
      in_review: ui.pillInfo,
      scheduled: ui.pillInfo,
      registered: ui.pillInfo,
      rejected: ui.pillDanger,
      cancelled: ui.pillDanger,
      blocked: ui.pillDanger,
      declined: ui.pillDanger,
      urgent: ui.pillDanger,
    }[status] || ui.pillNeutral;
  return <span className={tone}>{String(status || 'unknown').replace(/_/g, ' ')}</span>;
}

/**
 * A person, rendered the same way everywhere (forum authors, attendees,
 * reviewers). The API returns a denormalised `*Name` alongside the id; the id
 * is the fallback and always the tooltip.
 */
export function UserChip({ userId, name }) {
  const display = name || userId;
  if (!display) return null;
  return (
    <span
      className={cn(ui.pillNeutral, 'inline-block max-w-56 truncate align-middle')}
      title={userId || undefined}
    >
      {display}
    </span>
  );
}

/**
 * Numeric pill for unread/queue counts. Renders nothing at zero (an empty
 * badge is noise), caps the display at 99+, and always carries a spoken label
 * because "12" on its own means nothing out of context.
 */
export function CountBadge({ count, label }) {
  const value = Number(count);
  if (!Number.isFinite(value) || value <= 0) return null;
  return (
    <span className={ui.countBadge} aria-label={label || `${value} unread`}>
      {value > 99 ? '99+' : String(value)}
    </span>
  );
}

export function formatDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return iso;
  }
}

export function formatDateTime(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

/**
 * "3 min ago" for feed timestamps. Past week only - anything older reads
 * better as a date, and anything in the future (scheduled items) is always a
 * date. Small clock skew either way is "just now".
 */
export function formatRelative(iso) {
  if (!iso) return '—';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return String(iso);

  const seconds = Math.round((Date.now() - then) / 1000);
  if (seconds < 45 && seconds > -45) return 'just now';
  if (seconds < 0) return formatDate(iso);

  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} min ago`;

  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} hr${hours === 1 ? '' : 's'} ago`;

  const days = Math.round(hours / 24);
  if (days <= 7) return `${days} day${days === 1 ? '' : 's'} ago`;

  return formatDate(iso);
}

/**
 * Relative timestamp with the machine-readable value in `dateTime` and the
 * exact local time in the tooltip. Rendered statically - no ticking timer, so
 * it costs nothing in a long feed.
 */
export function RelativeTime({ iso }) {
  if (!iso) return <span className={ui.muted}>—</span>;
  return (
    <time dateTime={iso} title={formatDateTime(iso)}>
      {formatRelative(iso)}
    </time>
  );
}

export function UserSearchPicker({ onPick, exclude = [] }) {
  const [emailPrefix, setEmailPrefix] = useState('');
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState(null);

  async function onSearch(e) {
    e.preventDefault();
    const prefix = emailPrefix.trim();
    if (!prefix) return;
    setSearching(true);
    setError(null);
    try {
      const { users } = await lookupUsers(prefix);
      const filtered = (users || []).filter(u => !exclude.includes(u.userId));
      setResults(filtered);
    } catch (err) {
      setError(err);
    } finally {
      setSearching(false);
    }
  }

  return (
    <>
      <ErrorAlert error={error} />
      <form onSubmit={onSearch} className={cn(ui.row, 'items-end')}>
        <Field
          label="Search by email"
          placeholder="name@example.com"
          value={emailPrefix}
          onChange={e => setEmailPrefix(e.target.value)}
        />
        <button
          type="submit"
          className={cn(ui.btnSecondary, 'mb-4')}
          disabled={searching || !emailPrefix.trim()}
        >
          {searching ? 'Searching…' : 'Search'}
        </button>
      </form>
      {results === null ? (
        <EmptyState>Type an email prefix and search to find members.</EmptyState>
      ) : results.length === 0 ? (
        <EmptyState>No members match that email prefix.</EmptyState>
      ) : (
        <ul className="m-0 list-none p-0">
          {results.map(u => (
            <li key={u.userId} className="mb-4">
              <button
                type="button"
                className={cn(ui.btnSecondary, 'w-full justify-start')}
                onClick={() => {
                  onPick(u);
                  setResults(null);
                  setEmailPrefix('');
                }}
              >
                {u.displayName} - {u.email}
              </button>
            </li>
          ))}
        </ul>
      )}
    </>
  );
}
